// DocumentPreviewModal.tsx
"use client";
import { Modal, useMantineColorScheme } from "@mantine/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchContext } from "@/context/search-context";
import { FileOrFolder } from "../../../../types/FileOrFolder";
import { makeFileSource } from "./fileSource";
import { detectKind } from "./detect";
import { viewerFor } from "./registry";
import { downloadViaServer } from "./actions/download";
import { printPdfURL, waitForReadyState, triggerPrint_Safari, printHTML, printPdfURL_iOSCanvas, printImageSameTab_iOS } from "./actions/print";
import { highlightMatches, highlightMatchesHTML } from "@/utils/highlightMatches";
import { getUserDisplayName, truncateWithSmartDots } from "@/utils/general";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useDocUploaderContext } from "@/context/doc-uploader-context";
import { useGlobalContext } from "@/context/global-context";
import { usePathname } from "next/navigation";
import { useDispatch } from "react-redux";
import { AppDispatch, useAppSelector } from "@/redux/store";
import { fetchBcrFile } from "@/redux/features/bcr-inspect-slice";
import api from "@/axiosSetup";
import { renderAsync } from "docx-preview";
import { openPrintWindowNow_SafariSafe, truncateFileName } from "@/utils/printBCR";
import { useIsMobile } from "@/src/hooks/useIsMobile";
import Papa from "papaparse";
import { IMeetingMinutes } from "../../../../types/IMeetingMinutes";
import { useMinutesContext } from "@/context/meeting-minutes-context";
import { printTranscript } from "./actions/printTranscript";
import PdfPageDownload from "./PdfPageDownload";
import type { PreviewPdfDocument } from "./actions/pdfPageDownload";

// get extension safely
const getUrlExtension = (u?: string | null) => {
  if (!u) return null;
  try {
    const pathname = new URL(u).pathname;
    const last = pathname.split("/").pop() || "";
    const dot = last.lastIndexOf(".");
    if (dot === -1) return null;
    return last.slice(dot + 1).toLowerCase();
  } catch {
    return null;
  }
};

const normalizeExt = (ext?: string | null) => {
  if (!ext) return null;
  const map: Record<string, string> = { jpeg: "jpg", tif: "tiff" };
  return map[ext] || ext;
};

const getSignatureIcon = (color = "black") => (
  <img
    className="w-[1rem] invert"
    src='/vectors/signature-icon.svg'
    alt='signature-icon'
  />
);

// type Props = { file: FileOrFolder | IMeetingMinutes | null; isOpen: boolean; onClose: () => void; };
type Props = {
  file: FileOrFolder | null;
  isOpen: boolean;
  onClose: () => void;
};

export default function DocumentPreviewModal2({ file, isOpen, onClose }: Props) {
  const {
    searchInput: generalSearchInput,
    searchInputManageBCR,
    searchType,
    simpleSearchString,
  } = useSearchContext();
  const { searchInput: minutesSearchInput } = useMinutesContext();

  const source = useMemo(() => (file ? makeFileSource(file) : null), [file]);
  const kind = file?.type?.startsWith("audio/")
    ? "audio"
    : detectKind(source?.ext || file?.type || "");
  const Viewer = viewerFor[kind];
  const audioTranscript = file?.meetingMinuteContent
    ? file.meetingMinuteContent.extractedText
    : file?.content;

  // MY STUFF
  const {
    inspectOverlay,
    setSelectedItem,
    setActiveManageBCRTab,
    setOpenEditBCR,
    setOpenSignDS,
    setOpenSignManual,
    setLoader
  } = useGlobalContext();

  const { fileViewType, setSignerUserId } = useDocUploaderContext();
  const [previewPdf, setPreviewPdf] = useState<PreviewPdfDocument | null>(null);
  const [fileSize, setFileSize] = useState<string | null>(null);
  //Search
  const pathname = usePathname();
  // const { searchInput, searchInputManageBCR } = useSearchContext();
  // const searchKeywords = searchInput.filter(Boolean); // /search
  // const searchKeywordsManageBCR = searchInputManageBCR.filter(Boolean); // /manage-bcr
  //Redux
  const dispatch = useDispatch<AppDispatch>();
  const user = useAppSelector(state => state.authReducer.user);
  const mode = useAppSelector(state => state.fileExplorerReducer.mode); //search, explorer, meeting-minutes

  const isMobile = useIsMobile();
  const { colorScheme } = useMantineColorScheme();
  const isLight = colorScheme === "light";

  const pageWrapRef = useRef<HTMLDivElement | null>(null);
  // Keywords new
  // const keywords = useMemo(() => searchInput.filter(Boolean), [searchInput]);

  // DETERMINES! Use global state when in /file-explorer
  const isInspectorActive =
    !!inspectOverlay ||
    pathname.startsWith("/manage-bcr") ||
    pathname.startsWith("/manage-meeting-minute");

  const activeSearchInput = isInspectorActive
    ? searchInputManageBCR
    : (pathname === "/meeting-minutes" || mode === "meeting-minutes")
      ? minutesSearchInput
      : generalSearchInput;

  const keywordsRaw = useMemo(
    () => activeSearchInput.filter(Boolean),
    [activeSearchInput]
  );

  const isBcrPdfFile =
    file?.type === "bcr-pdf" ||
    !!file?.bcrContent?.signaturesMergedImg;

  const printBcrSafely = async () => {
    if (!file?._id) {
      toast.error("Missing BCR file id.");
      return;
    }

    try {
      const res = await api.post(
        `/api/bcrs/print-merged/${file._id}`,
        {
          supportingDocIds: [],
          includeMeetingMinute: false,
          // no bcrPages means all pages
        },
        { responseType: "blob" }
      );

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);

      await printPdfURL(url, `${file?.name || "bcr"}-print.pdf`);

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60_000);
    } catch (err) {
      console.error("Failed to prepare BCR for printing:", err);
      toast.error("Failed to prepare BCR for printing.");
    }
  };

  const printPdfExact = async () => {
    if (!source) return;

    await printPdfURL(
      source.url,
      `${file?.name || "document"}.pdf`
    );
  };

  const printPdfFitted = async () => {
  if (!source) return;

  const res = await api.post(
    "/api/print/fit-pdf",
    {
      url: source.url,
      name: `${file?.name || "document"}-print-safe.pdf`,
      targetPageSize: "AUTO",
    },
    { responseType: "blob" }
  );

  const blob = new Blob([res.data], { type: "application/pdf" });
  const printSafeUrl = URL.createObjectURL(blob);

  await printPdfURL(
    printSafeUrl,
    `${file?.name || "document"}-print-safe.pdf`
  );

  setTimeout(() => {
    URL.revokeObjectURL(printSafeUrl);
  }, 60_000);
};

  const keywords = useMemo(() => {
    // In BCR/MM inspector mode, always use the internal inspector keywords.
    // Do not let simpleSearchString override them.
    if (isInspectorActive) {
      return keywordsRaw;
    }

    if (searchType === "simple") {
      const phrase = (simpleSearchString || "").trim();
      return phrase ? [phrase] : [];
    }

    return keywordsRaw;
  }, [isInspectorActive, searchType, simpleSearchString, keywordsRaw]);
  // const activeSearchInput =
  //   (pathname === "/meeting-minutes" || mode === 'meeting-minutes') 
  //   ? minutesSearchInput : generalSearchInput;

  // // const keywordsRaw = useMemo(() => searchInput.filter(Boolean), [searchInput]);
  // const keywordsRaw = useMemo(
  //   () => activeSearchInput.filter(Boolean),
  //   [activeSearchInput]
  // );

  // const keywords = useMemo(() => {
  //   // if (pathname === "/file-explorer" && mode !== "search") return [];
  //   if (searchType === "simple") {
  //     const phrase = (simpleSearchString || "").trim();
  //     return phrase ? [phrase] : [];
  //   }
  //   return keywordsRaw;
  // }, [pathname, mode, searchType, simpleSearchString, keywordsRaw]);

  useEffect(() => {
    if (!pageWrapRef.current) return;
    const host = pageWrapRef.current;

    const kids = Array.from(host.children);

    kids.forEach(child => {
      // Don't double-wrap if it's already wrapped
      if ((child as HTMLElement).dataset?.bcrPageframe === "1") return;

      // Make a wrapper div for this "page"
      const wrapper = document.createElement("div");
      wrapper.dataset.bcrPageframe = "1";

      // White page background so the border is visible in light+dark
      wrapper.style.backgroundColor = "#ffffff";

      // A tiny radius just so it looks like a sheet, optional
      wrapper.style.borderRadius = "2px";

      // Prevent overflow from canvases/images
      wrapper.style.overflow = "hidden";

      // So page is only as wide as its content
      wrapper.style.width = "fit-content";

      // ← THIS is the main change: border only
      // Light mode: light gray border
      // Dark mode: slightly brighter gray so it still contrasts against dark bg
      wrapper.style.border = isLight
        ? "1px solid #a3a3a3"      // ~gray-400
        : "1px solid rgb(156 163 175)"; // ~gray-400/500 visible on dark

      // Insert wrapper before the page, then move the page inside it
      host.insertBefore(wrapper, child);
      wrapper.appendChild(child);
    });
  }, [source, isLight]);


  // Pick the source URL from file.url or bcrContent.signaturesMergedImg
  // const realUrl =
  //   file?.url
  //     ? file.url
  //     : file?.bcrContent?.signaturesMergedImg
  //     ? file.bcrContent?.signaturesMergedImg
  //     : null;
  const hasBcrContent = (
    value: FileOrFolder | IMeetingMinutes | null
  ): value is FileOrFolder & { bcrContent?: { signaturesMergedImg?: string } } => {
    return !!value && "bcrContent" in value;
  };

  const realUrl =
    file?.url
      ? file.url
      : hasBcrContent(file)
      ? file.bcrContent?.signaturesMergedImg ?? null
      : null;

  const ext = normalizeExt(getUrlExtension(realUrl)) ?? file?.type ?? "unknown";

  // useEffect(() => {    
  //   const fetchSizeURL = async () => {
  //     if (!file || !file.url || !file.url.includes('cloudinary.com')) return;
  //     const relativePath = file.url!
  //       .split('/upload/')[1]
  //       .split('/')
  //       .slice(1) // drop the version like 'v1746211984'
  //       .join('/')
  //       .replace(/\.[^/.]+$/, ''); // remove .pdf, .jpg, etc.
  
  //     try {
  //       const { data } = await api.get(`/api/cloudinary/${relativePath}`);
  //       if (data?.sizeKB) {
  //         setFileSize(data.sizeKB);
  //       }
  //     } catch (err) {
  //       console.warn("Failed to get Cloudinary file size:", err);
  //     }
  //   };
    
  //   const fetchSizeExtractSignature = async () => {
  //     if (!file || !file.singleUploadedBCRSignature?.extractedSignatureImg || !file.singleUploadedBCRSignature?.extractedSignatureImg?.includes('cloudinary.com')) return;
  //     const relativePath = file.singleUploadedBCRSignature?.extractedSignatureImg!
  //       .split('/upload/')[1]
  //       .split('/')
  //       .slice(1) // drop the version like 'v1746211984'
  //       .join('/')
  //       .replace(/\.[^/.]+$/, ''); // remove .pdf, .jpg, etc.
  
  //     try {
  //       const { data } = await api.get(`/api/cloudinary/${relativePath}`);
  //       if (data?.sizeKB) {
  //         setFileSize(data.sizeKB);
  //       }
  //     } catch (err) {
  //       console.warn("Failed to get Cloudinary file size:", err);
  //     }
  //   };
  
  //   if (fileViewType === 'url') {
  //     fetchSizeURL();
  //   }
    
  //   if (fileViewType === 'extracted-signature') {
  //     fetchSizeExtractSignature();
  //   }
  // }, [file?.url, file?.singleUploadedBCRSignature?.extractedSignatureImg, fileViewType]);
  
  useEffect(() => {
    const fetchSize = async () => {
      if (!realUrl || !realUrl.includes("cloudinary.com")) return;

      // // Extract relative path after /upload/
      // const relativePath = realUrl
      //   .split("/upload/")[1]
      //   ?.split("/")
      //   .slice(1) // drop version (e.g. v12345)
      //   .join("/")
      //   .replace(/\.[^/.]+$/, ""); // remove extension

      // if (!relativePath) return;

      try {
        const { data } = await api.get(`/api/cloudinary/${realUrl}`);
        // const { data } = await api.get(`/api/cloudinary/${relativePath}`);
        if (data?.sizeKB) setFileSize(data.sizeKB);
      } catch (err) {
        console.warn("Failed to get Cloudinary file size:", err);
      }
    };

    fetchSize();
  }, [realUrl]);

  const handleDownload = () => {
    if (!source) return;
    downloadViaServer(source.url, file?.name || "file", source.ext || "dat");
  };

  // SAFARI PRINT STUFF /////////////////////////////////////////////////////
    const isSafari = () => {
      if (typeof navigator === 'undefined') return false;
      const ua = navigator.userAgent;
      // covers iOS & macOS Safari (not Chrome/Firefox on iOS)
      return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
    };

    const safeName = truncateFileName(file?.name!);
    const safari = isSafari();
    
    const isIOS = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);


  const handlePrint = async () => {
    try {
      if (kind === "audio") {
        return await printTranscript(audioTranscript, file?.name || "Audio transcript", toast);
      }
      if (!source) return;

      // Helper: get a clean extension to branch on
      const ext = (source.ext || "").toLowerCase();

      // if (ext === "pdf") {
      //   if (isSafari() && isIOS()) {
      //     return await printPdfURL_iOSCanvas(source.url, `${file?.name || "document"}`);
      //   }

      //   const res = await api.post(
      //     "/api/print/fit-pdf",
      //     {
      //       url: source.url,
      //       name: `${file?.name || "document"}-print-safe.pdf`,
      //     },
      //     { responseType: "blob" }
      //   );

      //   const blob = new Blob([res.data], { type: "application/pdf" });
      //   const printSafeUrl = URL.createObjectURL(blob);

      //   await printPdfURL(printSafeUrl, `${file?.name || "document"}-print-safe.pdf`);

      //   setTimeout(() => {
      //     URL.revokeObjectURL(printSafeUrl);
      //   }, 60_000);

      //   return;
      // }
      if (ext === "pdf") {
        if (isSafari() && isIOS()) {
          return await printPdfURL_iOSCanvas(
            source.url,
            `${file?.name || "document"}`
          );
        }

        // System BCR PDFs use your BCR merge/print route.
        if (isBcrPdfFile) {
          return await printBcrSafely();
        }

        // Uploaded/normal PDFs get normalized to a print-safe paper size.
        return await printPdfFitted();
      }
      // Images: unchanged
      // if (/^(png|jpg|jpeg|webp|gif|bmp|tiff|svg|jfif)$/.test(ext)) {
      //   return printHTML(`<img src="${source.url}" alt="${escapeHtml(file?.name || "image")}" />`,
      //     file?.name || "image");
      // }
      if (safari && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
        return printImageSameTab_iOS(source.url, safeName);
      }
      if (/^(png|jpg|jpeg|webp|gif|bmp|tiff|svg|jfif)$/.test(ext)) {
        if (safari) {
          const portal = openPrintWindowNow_SafariSafe(safeName || 'image');
          if (!portal || !portal.root) { toast?.error?.("Popup blocked. Please allow popups to print."); return; }
          const { win, doc, root, cleanup } = portal;
  
          // Write the image into the popup
          root.innerHTML = `
            <img id="__pimg" src="${source.url}" alt="${escapeHtml(file?.name || "image")}"
                style="max-width:100%;height:auto;display:block;margin:0 auto;" />
          `;
  
          const img = root.querySelector('#__pimg') as HTMLImageElement | null;
          const go = async () => { await waitForReadyState(doc); triggerPrint_Safari(win, cleanup); };
  
          if (!img) {
            // Fallback if the element wasn't created for some reason
            await go();
            return;
          }
          if (img.complete) {
            await go();
          } else {
            img.onload = () => { go(); };
            img.onerror = () => { go(); }; // still print a blank page rather than hang
          }
          return;
        }
  
        // Non-Safari: your existing approach is fine
        return printHTML(
          `<img src="${source.url}" alt="${escapeHtml(file?.name || "image")}" />`,
          safeName,
          toast
        );
      }

      const sameOrigin = (() => {
        try { return new URL(source.url, window.location.href).origin === window.location.origin; }
        catch { return false; }
      })();
 
      // ---------- TXT ----------
      if (ext === "txt") {
        if (safari) {
          // Use the Safari-safe opener (no noopener, full HTML skeleton + document.close()).
          const portal = openPrintWindowNow_SafariSafe(safeName || "text");
          if (!portal || !portal.root) { toast?.error?.("Popup blocked. Please allow popups to print."); return; }
          const { win, doc, root, cleanup } = portal;

          try {
            // Try direct fetch first; fall back to API proxy if you pass apiBaseUrl.
            let txt: string | null = null;
            try {
              const res = await fetch(source.url, { credentials: sameOrigin ? "include" : "omit" });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              txt = await res.text();
            } catch (err) {
              if (process.env.NEXT_PUBLIC_API_BASE_URL) {
                const proxied = `${process.env.NEXT_PUBLIC_API_BASE_URL.replace(/\/$/, "")}/file/print-proxy?url=${encodeURIComponent(source.url)}&type=txt`;
                const res2 = await fetch(proxied, { credentials: "include" });
                if (!res2.ok) throw new Error(`Proxy HTTP ${res2.status}`);
                txt = await res2.text();
              } else {
                throw err;
              }
            }

            // Inject content + minimal styles (pre-wrap so long lines don’t blow up pages).
            root.innerHTML = `
              <pre style="
                margin:0;
                white-space: pre-wrap;
                word-wrap: break-word;
                overflow-wrap: anywhere;
                line-height:1.45;
                font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
              ">${escapeHtml(txt || "")}</pre>
            `;
            const style = doc.createElement("style");
            style.textContent = `
              @media print { @page {  } body { background:#fff; } }
            `;
            doc.head.appendChild(style);

            // Safari needs fonts.ready too — not just images.
            await waitForReadyState(doc);

            // Extra safety so you can print more than once without refresh.
            try { (win as any).onpagehide = cleanup; } catch {}
            try { (win as any).onunload   = cleanup; } catch {}

            triggerPrint_Safari(win, cleanup);
          } catch (e) {
            console.error("TXT print failed (Safari):", e);
            root.innerHTML = `<p style="padding:16px;color:#b00">Couldn't load TXT for printing.</p>`;
            triggerPrint_Safari(win, cleanup);
          }
          return;
        }

        // NON-SAFARI: keep as-is
        try {
          const res = await fetch(source.url, { credentials: sameOrigin ? "include" : "omit" });
          const text = await res.text();
          return printHTML(`<pre>${escapeHtml(text)}</pre>`, safeName, toast);
        } catch {
          toast?.error?.("Couldn't load TXT for printing.");
          return;
        }
      }
      // if (ext === "txt") {
      //   // Same-origin check so cookies are sent only when needed
      //   const sameOrigin = (() => {
      //     try { return new URL(source.url, window.location.href).origin === window.location.origin; }
      //     catch { return false; }
      //   })();

      //   const resp = await fetch(source.url, { credentials: sameOrigin ? "include" : "omit" });
      //   const text = await resp.text();

      //   // Render as normal text with <br> for line breaks to paginate cleanly
      //   const html = `
      //     <div
      //       style="
      //         white-space: normal;
      //         line-height: 1.4;
      //         overflow-wrap: anywhere;
      //         word-break: break-word;
      //         page-break-inside: auto;
      //         max-width: 100%;
      //         margin: 0;
      //       "
      //     >${escapeHtml(text).replace(/\r\n|\r|\n/g, "<br>")}</div>
      //   `;

      //   return printHTML(html, file?.name || "text");
      // }

      // ---------- DOCX ----------
      if (ext === "docx") {
        if (safari) {
          // Open popup synchronously (no awaits before this call)
          const portal = openPrintWindowNow_SafariSafe(safeName);
          if (!portal || !portal.root) { toast?.error?.("Popup blocked. Please allow popups to print."); return; }
          const { win, doc, root, cleanup } = portal;

          try {
            // fetch DOCX and render inside the popup DOM
            const sameOrigin = (() => {
              try { return new URL(source.url, window.location.href).origin === window.location.origin; }
              catch { return false; }
            })();

            const resp = await fetch(source.url, { credentials: sameOrigin ? "include" : "omit" });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const ab = await resp.arrayBuffer();

            await renderAsync(ab, root, undefined, { className: "docx", inWrapper: false });

            const docxEl = root.querySelector(".docx") as HTMLElement | null;
            if (docxEl) {
              docxEl.style.paddingBottom = "0";
              docxEl.style.marginBottom  = "0";
              (docxEl.style as any).minHeight = "unset";
            }
            doc.querySelectorAll("footer").forEach(el => (el as HTMLElement).style.marginBottom = "0");

            await waitForReadyState(doc);
            triggerPrint_Safari(win, cleanup);
          } catch (e) {
            console.error("DOCX print failed (Safari):", e);
            root.innerHTML = `<p style="padding:16px;color:#b00">Failed to print DOCX.</p>`;
            triggerPrint_Safari(win, cleanup);
          }
          return;
        }

        // Non-Safari: your existing off-DOM render then printHTML
        const resp = await fetch(source.url);
        const blob = await resp.blob();
        const ab   = await blob.arrayBuffer();

        const temp = document.createElement("div");
        await renderAsync(ab, temp, undefined, { className: "docx", inWrapper: false });

        const docxEl = temp.querySelector(".docx") as HTMLElement | null;
        if (docxEl) {
          docxEl.style.paddingBottom = "0";
          docxEl.style.marginBottom  = "0";
          (docxEl.style as any).minHeight = "unset";
        }
        temp.querySelectorAll("footer").forEach((el) => (el as HTMLElement).style.marginBottom = "0");

        return printHTML(temp.innerHTML, file?.name || "document");
      }

      // ---------- CSV ----------
      if (ext === "xls" || ext === "xlsx" || ext === "csv") {
      const sameOrigin = (() => {
        try { return new URL(source.url, window.location.href).origin === window.location.origin; }
        catch { return false; }
      })();

      if (safari) {
        const portal = openPrintWindowNow_SafariSafe(safeName);
        if (!portal || !portal.root) { toast?.error?.("Popup blocked. Please allow popups to print."); return; }
        const { win, doc, root, cleanup } = portal;

        try {
          const res = await fetch(source.url, { credentials: sameOrigin ? "include" : "omit" });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);

          if (ext === "csv") {
            const csv = await res.text();
            root.innerHTML = csvToPrintableHtml(csv, safeName);
          } else {
          // if (ext === "csv") {
          //   // CSV → table (no SheetJS needed)
          //   const csv = await res.text();
          //   const rows = parseCsv(csv);
          //   const table = rows
          //     .map(r => `<tr>${r.map(c => `<td>${escapeHtml(c)}</td>`).join("")}</tr>`)
          //     .join("");
          //   root.innerHTML = `<table><tbody>${table}</tbody></table>`;
          // } else {
            // XLS/XLSX → use SheetJS in parent, not inside popup
            const ab = await res.arrayBuffer();
            const XLSXmod = await import("xlsx");
            const XLSX = (XLSXmod as any).default || XLSXmod;

            const wb = XLSX.read(ab, { type: "array" });
            const htmlParts: string[] = [];
            wb.SheetNames.forEach((sn: string, i: number) => {
              const sh = wb.Sheets[sn];
              const html = XLSX.utils.sheet_to_html(sh, {
                header: `<h3 style="margin:0 0 8px 0">${escapeHtml(safeName)} — ${escapeHtml(sn)}</h3>`
              });
              htmlParts.push(`<section class="sheet ${i > 0 ? "page-break" : ""}">${html}</section>`);
            });
            root.innerHTML = `<div class="sheets">${htmlParts.join("")}</div>`;
          }

          const style = doc.createElement("style");
          style.textContent = `
            @media print { @page { margin: 12mm; } .page-break { page-break-before: always; } }
            body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; }
            table { border-collapse: collapse; width: 100%; }
            td, th { border: 1px solid #999; padding: 4px; vertical-align: top; }
          `;
          doc.head.appendChild(style);

          await waitForReadyState(doc);
          triggerPrint_Safari(win, cleanup);
        } catch (e) {
          console.error("XLS/XLSX/CSV print failed (Safari):", e);
          root.innerHTML = `<p style="padding:16px;color:#b00">Failed to print spreadsheet.</p>`;
          triggerPrint_Safari(win, cleanup);
        }
        return;
      }

      // --- NON-SAFARI path: keep your existing printHTML approach or similar ---
      try {
        const res = await fetch(source.url, { credentials: sameOrigin ? "include" : "omit" });
        const csv = await res.text();
        return printHTML(csvToPrintableHtml(csv, safeName), safeName, toast);
      } catch {
        toast?.error?.("Couldn't load CSV for printing.");
        return;
      }
    }
      // If you also want to try printing unknown text types by fetching as text:
      // (optional fallback)
      try {
        const resp = await fetch(source.url);
        const maybeText = await resp.text();
        if (maybeText && /^[\s\S]*$/.test(maybeText)) {
          return printHTML(`<pre>${escapeHtml(maybeText)}</pre>`, file?.name || "document");
        }
      } catch {
        // ignore and fall through
      }

      toast.error("Unsupported file type for printing.");
    } catch (err) {
      console.error("Print failed:", err);
      toast.error("Failed to print this document.");
    }
  };

  const adderName = getUserDisplayName(file?.createdUser);

  return (
    <Modal zIndex={10000000000} fullScreen={isMobile} opened={isOpen} onClose={onClose} size={870} centered withCloseButton={false}
    // title="Document Preview"
      title={
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            width: "100%",
          }}
        >
          <span>
            Document Preview
          </span>
          <button
            onClick={onClose}
            className="z-[1000] absolute top-[10px] right-4 px-2 py-3 rounded-md bg-red-600 hover:bg-red-700 text-white">
            Close
          </button>
        </div>
      }
      styles={{
          header: {
            // display: 'none',
          },
          content: {
            // overflowY: 'hidden',
            // height: 'auto',
            // maxHeight: '94vh',
            maxHeight: '100vh',
            // overflowY: 'visible',
            // WebkitOverflowScrolling: "touch",
          },
      }}>
      {/* <button
        onClick={onClose}
        className="z-[1000] absolute top-6 sm:top-6 right-4 px-2 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white">
        Close
      </button> */}
      <div className="flex flex-wrap items-start justify-between gap-2 mt-1 sm:mt-0 mb-1 relative">
        {/* LEFT: file info */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="font-semibold text-sm sm:text-base text-gray-700 dark:text-white">
            <span
              className="break-words"
              dangerouslySetInnerHTML={{
                __html: highlightMatchesHTML(
                  truncateWithSmartDots(file?.name ?? "Unknown File", isMobile ? 20 : 50),
                  keywords
                ),
              }}
            />
            .{ext}
            {fileSize && (
              <span className="ml-2 text-gray-500 font-normal dark:text-white">
                ({fileSize})
              </span>
            )}
          </div>

          <span className="text-[12px] break-words">
            Added: {highlightMatches(file?.createdAt ? format(new Date(file.createdAt), "MMMM d, yyyy") : "", keywords)}
          </span>
          {/* <span className="text-[12px] break-words">
            Adder: {highlightMatches(`${file?.createdUser?.firstName} ${file?.createdUser?.lastName}`, keywords)}
          </span> */}
         <span className="text-[12px] break-words">
            Adder:{" "}
            {adderName ? (
              highlightMatches(adderName, keywords)
            ) : (
              <span className="text-red-500">User not found</span>
            )}
          </span>
        </div>

        {/* <div id='top-right-btns' className='grid grid-cols-1 sm:flex items-center gap-2 flex-wrap'> */}
        {/* RIGHT: buttons */}
      <div id="top-right-btns" className="flex flex-shrink-0 flex-wrap max-w-full items-center gap-2">
        <button onClick={handleDownload} className="text-sm w-fit bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded">
          Download
        </button>
        {kind === "pdf" && source && (
          <PdfPageDownload key={source.url} active={isOpen} filename={file?.name || "document"}
            pdf={previewPdf?.url === source.url ? previewPdf : null} />
        )}
        <button onClick={handlePrint} className="text-sm w-fit bg-blue-600 hover:bg-blue-700 text-white py-2 px-3 rounded">
          {kind === "audio" ? "Print Transcript" : "Print Doc"}
        </button>
      </div>
      </div>
      {!source ? (
        <div className="p-4 text-gray-500">No file source.</div>
      ) : (
        <Viewer url={source.url} keywords={keywords} alt={file?.name} transcript={audioTranscript}
          onPdfReady={setPreviewPdf} />
      )}
    </Modal>
  );
}

function csvToPrintableHtml(csvText: string, title?: string) {
  const parsed = Papa.parse<string[]>(csvText, {
    skipEmptyLines: true,
  });

  const rows = (parsed.data || []).filter((row) => Array.isArray(row) && row.length > 0);

  if (!rows.length) {
    return `<div style="padding:16px;">No CSV data found.</div>`;
  }

  const header = rows[0];
  const body = rows.slice(1);

  const thead = `
    <thead>
      <tr>
        ${header.map((cell) => `<th>${escapeHtml(String(cell ?? ""))}</th>`).join("")}
      </tr>
    </thead>
  `;

  const tbody = `
    <tbody>
      ${body
        .map(
          (row) => `
            <tr>
              ${row.map((cell) => `<td>${escapeHtml(String(cell ?? ""))}</td>`).join("")}
            </tr>
          `
        )
        .join("")}
    </tbody>
  `;

  return `
    <div>
      ${
        title
          ? `<h2 style="margin:0 0 12px 0; font-size:18px;">${escapeHtml(title)}</h2>`
          : ""
      }
      <table style="width:100%; border-collapse:collapse; font-size:13px;">
        ${thead}
        ${tbody}
      </table>
    </div>
  `;
}

const escapeHtml = (s: string) =>
  s.replace(/[&<>"']/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[m]!));
